import { api } from '../core/client';
import { wirePhone } from '../core/wire';

/** A login-able staff profile (no secrets) from `GET /api/auth/staff`. */
export interface ApiAuthUser {
  id: string;
  username: string;
  fullName: string;
  role: string;
  roleCode: string;
}

export interface ApiAuthMenu {
  code: string;
  name: string;
  module: string;
  icon: string;
  url: string;
  sortOrder: number;
}

/** What login returns: identity, token, and the flat permission codes. */
export interface ApiLoginResponse {
  token: string;
  /** Single-use token that silently renews `token`; the axios client uses it on a 401. */
  refreshToken: string;
  /** Hard end of the sign-in (ISO), renewals included. */
  sessionExpiresAt?: string;
  user: ApiAuthUser;
  permissions: string[];
  menus: ApiAuthMenu[];
}

/** Result of requesting a customer OTP. `devCode` is only present in the dev stub (no SMS). */
export interface ApiSendOtpResponse {
  sent: boolean;
  devCode: string;
}

/** The signed-in customer identity returned after a verified OTP. */
export interface ApiCustomerAuth {
  id: number;
  name: string;
  initials: string;
  phone: string;
  tier: string;
  points: number;
  token: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string | null;
  result: T;
}

/** Authentication API — staff picker, PIN quick-login, username/password login, and customer OTP. */
export const authService = {
  staff(): Promise<ApiAuthUser[]> {
    return api.get<ApiEnvelope<ApiAuthUser[]>>('/auth/staff').then((r) => r.data.result);
  },
  login(username: string, password: string): Promise<ApiLoginResponse> {
    return api.post<ApiEnvelope<ApiLoginResponse>>('/auth/login', { username, password }).then((r) => r.data.result);
  },
  pinLogin(username: string, pin: string): Promise<ApiLoginResponse> {
    return api.post<ApiEnvelope<ApiLoginResponse>>('/auth/pin-login', { username, pin }).then((r) => r.data.result);
  },
  /**
   * Dine-in guest sign-in — name + mobile, NO OTP, for a guest who scanned a table QR.
   * The table is the credential: the server only issues a token if it resolves and is enabled.
   */
  guest(tableCode: string, name: string, phone: string): Promise<ApiCustomerAuth> {
    return api
      .post<ApiEnvelope<ApiCustomerAuth>>('/auth/guest', { tableCode, name, phone: wirePhone(phone) })
      .then((r) => r.data.result);
  },
  /** Revoke this device's refresh token, so signing out ends the session server-side too. */
  signOut(refreshToken: string): Promise<void> {
    return api.post('/auth/sign-out', { refreshToken }).then(() => undefined).catch(() => undefined);
  },
  /** Customer app: request a phone OTP (dev stub returns the code). */
  sendOtp(phone: string): Promise<ApiSendOtpResponse> {
    return api.post<ApiEnvelope<ApiSendOtpResponse>>('/auth/send-otp', { phone: wirePhone(phone) }).then((r) => r.data.result);
  },
  /** Customer app: verify the OTP and sign the customer in. An optional name names a brand-new
   *  customer so their orders/bills show who they are (mobile comes from the phone step). */
  verifyOtp(phone: string, code: string, name = ''): Promise<ApiCustomerAuth> {
    return api.post<ApiEnvelope<ApiCustomerAuth>>('/auth/verify-otp', { phone: wirePhone(phone), code, name }).then((r) => r.data.result);
  }
};
